import { Button, Card, Container, Input, Modal, Typography } from '@design-system';
import { FilesComponentsProps, useFlowTs } from '@hooks';
import React, { useRef } from 'react';
import { BiPlus } from 'react-icons/bi';
import { IoMdClose } from 'react-icons/io';
import { FileSkeleton } from './FileSkeleton';
import './FileUploader.scss';
import { useFileUploader } from './useFileUploader';

export const FileUploader: React.FC<FilesComponentsProps & { overflow?: boolean; disabled?: boolean }> = ({
    files,
    setFiles,
    overflow = true,
    disabled = false
}) => {
    const { flow, handleFileDelete, loading } = useFlowTs({ setFiles });
    const { fileModalOpen, handleOpenFileModal, handleCloseFileModal } = useFileUploader();

    const inputRef = useRef<HTMLInputElement>(null);

    const handleOpenFileBrowser = () => inputRef.current?.click();

    if (!flow) return null;

    return (
        <Container className={`file-uploader ${overflow ? 'overflow' : ''}`} padding="none">
            <div className={`file-uploader-container ${overflow ? 'overflow' : ''}`}>
                {loading && <FileSkeleton />}
                {!loading && (
                    <Card filled={overflow} row>
                        {files &&
                            files.map((file) => (
                                <Container
                                    key={file.filename}
                                    padding="none"
                                    className="file-container"
                                    onClick={() => handleOpenFileModal(file)}
                                >
                                    {file.mime_type.startsWith('image/') && <img src={file.url} alt={file.filename} />}
                                    {file.mime_type.startsWith('video/') && <video src={file.url} controls autoPlay={false} />}
                                    {!disabled && (
                                        <Button
                                            icon={<IoMdClose />}
                                            iconSize={20}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const flowFile = flow.getFromUniqueIdentifier(file.uniqueIdentifier);
                                                if (flowFile) {
                                                    handleFileDelete(flowFile);
                                                }
                                            }}
                                            iconColor="grey"
                                            variant="secondary"
                                            color="grey"
                                            size="small"
                                            className="remove-file-button"
                                        />
                                    )}
                                </Container>
                            ))}
                        {!disabled && (
                            <Button
                                icon={<BiPlus />}
                                iconSize={files.length > 0 ? 26 : 22}
                                onClick={handleOpenFileBrowser}
                                iconColor={'green'}
                                variant="secondary"
                                color="marine"
                                size="small"
                                className={`add-file-button ${files.length > 0 ? 'reduced' : ''} ${overflow ? 'overflow' : ''}`}
                            >
                                {files.length > 0 ? <></> : <Typography intrinsic="h4">Ajouter des fichiers</Typography>}
                            </Button>
                        )}
                    </Card>
                )}
            </div>

            <Input
                id="file-uploader"
                className="file-input"
                type="file"
                multiple
                ref={inputRef}
                placeholder="Ajouter un fichier"
                capture="environment"
                accept="image/*, video/*"
                value=""
                onValueChange={() => {}}
                disabled={disabled}
                style={{ display: 'none' }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const targetFiles: FileList | null = e.target.files;
                    if (targetFiles && flow) {
                        Array.from(targetFiles).forEach((file) => {
                            flow.addFile(file);
                        });
                    }
                }}
            />

            <Modal
                open={fileModalOpen !== undefined}
                setClose={handleCloseFileModal}
                title={fileModalOpen?.original_filename || 'Fichier'}
                footer={
                    <div className="modal-footer">
                        {!disabled && (
                            <Button
                                color="grey"
                                variant="tertiary"
                                icon={<IoMdClose />}
                                iconPosition="right"
                                iconSize={20}
                                onClick={() => {
                                    if (!fileModalOpen) {
                                        handleCloseFileModal();
                                        return;
                                    }
                                    const flowFile = flow.getFromUniqueIdentifier(fileModalOpen.uniqueIdentifier);
                                    if (flowFile) {
                                        handleFileDelete(flowFile);
                                    }
                                    handleCloseFileModal();
                                }}
                            >
                                <Typography intrinsic="h4">Supprimer</Typography>
                            </Button>
                        )}
                        <Button color="green" variant="secondary" size="small" onClick={handleCloseFileModal}>
                            <Typography intrinsic="h4">Fermer</Typography>
                        </Button>
                    </div>
                }
            >
                {fileModalOpen && (
                    <Container>
                        <img src={fileModalOpen.url} alt={fileModalOpen.original_filename} className="file-modal-image" />
                    </Container>
                )}
            </Modal>
        </Container>
    );
};
